import { Button } from "@xola/ui-kit";
import { IFilterParams } from "ag-grid-community";
import { convertAnyStringToFormattedTime } from "common";
import { isEmpty } from "lodash";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Merchandise, Product, ProductObject, Report, ReportObject, SelectionState } from "types";
import shallow from "zustand/shallow";
import { ProductSelector } from "../../../Common/ProductSelector";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { getFormattedFilterModel, getReportFilterState } from "../CustomReportGrid";
import { ReportFilter, ReportFilterOperator, ReportFilterState, ReportFilterValue } from "../ReportFilterState";
import { isInventoryTracked } from "@/models/Merchandise";
import { formatCreatedAtEndDate } from "@/models/Report";
import { useCustomReportStore } from "@/stores/customReportFilterStore";

const isTrackedMerchandiseProduct = (product: Product) => isInventoryTracked(product as Merchandise);

interface CustomProductFilterProps {
    collections?: ProductObject[];
    filterProduct?: (product: Product) => boolean;
}

export const CustomProductFilter = forwardRef((props: IFilterParams & CustomProductFilterProps, ref) => {
    const report = props.colDef.filterParams?.report as Report;
    const [initialFilter, filter, setCurrentFilter] = useCustomReportStore(
        (state) => [state.initialFilter, state.currentFilter, state.setCurrentFilter],
        shallow,
    );
    const field = props.colDef.field ?? "product";
    const defaultValue =
        Array.isArray(filter[field]?.value) && (filter[field]?.value as ReportFilterValue[])?.length > 0
            ? { all: false, selected: (filter[field]?.value as ReportFilterValue[]).map(String) }
            : { all: true, selected: [] };

    const [value, setValue] = useState<SelectionState>(defaultValue);

    useImperativeHandle(ref, () => ({
        isFilterActive() {
            if (Array.isArray(value.selected) && value.selected.length === 0) {
                console.log("%c isFilterActive: false", "color: orange");
                return false;
            }

            return !!value;
        },

        doesFilterPass() {
            return true; // Disables client-side filtering
        },

        getModel(): ReportFilter {
            if (Array.isArray(value.selected) && value.selected.length === 0) {
                console.log("%cThis should not be run", "color: red", value);
            }

            return {
                operator: ReportFilterOperator.IN,
                value: value.selected,
            };
        },

        setModel(model: { in: ReportFilterOperator; value: string[] }) {
            if (!model) {
                console.log("%cShould not set filter because model.filter is undef", "color:#CC6600", model);
                setValue({ all: true, selected: [] });
                return false;
            }

            setValue({ all: model?.value?.length === 0 || !model?.value, selected: model?.value ?? [] });
        },
    }));

    const handleChange = (selectedState: SelectionState) => {
        setValue(selectedState);
    };

    const handleFilterChange = (filter: ReportFilterState) => {
        const localFilter = { ...filter };

        if (localFilter.product?.operator === ReportFilterOperator.IN && isEmpty(localFilter.product?.value)) {
            // Hack for removing "product[in]=[]" from the query params because Rushi can't figure out how to do it
            delete localFilter.product;
        }

        if (localFilter.partner?.operator === ReportFilterOperator.IN && isEmpty(localFilter.partner?.value)) {
            delete localFilter.partner;
        }

        if (
            localFilter["items.product"]?.operator === ReportFilterOperator.IN &&
            isEmpty(localFilter["items.product"]?.value)
        ) {
            delete localFilter["items.product"];
        }

        if (
            localFilter["lineItems.coupon"]?.operator === ReportFilterOperator.IN &&
            isEmpty(localFilter["lineItems.coupon"]?.value)
        ) {
            delete localFilter["lineItems.coupon"];
        }

        if (report.object === ReportObject.TICKET_REDEMPTION && localFilter.createdAt) {
            localFilter.createdAt.value = formatCreatedAtEndDate(localFilter.createdAt.value as string[]);
        }

        if (localFilter?.arrivalTime) {
            localFilter.arrivalTime = {
                ...localFilter.arrivalTime,
                value: convertAnyStringToFormattedTime((localFilter?.arrivalTime.value as string).toString()), // TODO: FIX ME: I think we can just use `now()` here
            };
        }

        if (
            report.object === ReportObject.RESERVATIONS &&
            localFilter.status &&
            localFilter.status.operator === ReportFilterOperator.NIN
        ) {
            // Old saved reservations reports has NIN filter, now it is changed to IN filter, so removed old NIN
            delete localFilter.status;
        }

        if (report.object === ReportObject.RESERVATIONS_BY_LISTING && localFilter.arrivalDate) {
            // Filter arrival date by Item level also
            localFilter.items_arrivalDate = localFilter.arrivalDate;
        }

        setCurrentFilter(localFilter);
    };

    const handleApply = () => {
        const model = {
            operator: ReportFilterOperator.IN,
            value: value.selected,
        };
        const formattedFilterModel = getFormattedFilterModel({ [field]: model }, filter, initialFilter, report);
        handleFilterChange(getReportFilterState(formattedFilterModel));
        props.api.refreshHeader();
    };

    const handleCancel = () => {
        props.api.refreshHeader();
    };

    const isMerchandiseOnly = props.collections?.length === 1 && props.collections[0] === ProductObject.WARE;

    return (
        <div className="flex w-[500px] flex-col justify-between rounded-full p-2">
            <ProductSelector
                includeDeletedExperiences={!isMerchandiseOnly}
                value={value}
                collections={props.collections ?? [ProductObject.EXPERIENCE]}
                filterProduct={props.filterProduct}
                onChange={handleChange}
            />
            <div className="space-x-2 p-2 text-right">
                <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>

                <Button onClick={handleApply}>Apply</Button>
            </div>
        </div>
    );
    // return ReactDOM.createPortal(element, document.querySelector(".ag-theme-alpine").parentElement?.parentElement);
});

export const CustomMembershipProductFilter = forwardRef((props: IFilterParams, ref) => {
    return <CustomProductFilter {...props} ref={ref} collections={[ProductObject.MEMBERSHIP]} />;
});

export const CustomMerchandiseProductFilter = forwardRef((props: IFilterParams, ref) => {
    const trackedOnly = props.colDef.filterParams?.trackedOnly;

    return (
        <CustomProductFilter
            {...props}
            ref={ref}
            collections={[ProductObject.WARE]}
            filterProduct={trackedOnly ? isTrackedMerchandiseProduct : undefined}
        />
    );
});
